import {
  CONTRACT_TYPE_LABELS,
  DO_CONTRACT_TYPES,
} from "@etudescarre/constants";
import { Button, cn, Select, type SelectOption } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  PlusIcon,
  ProhibitIcon,
  TrashIcon,
  UserPlusIcon,
  UsersThreeIcon,
  XIcon,
} from "@phosphor-icons/react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { cellInputClass as inputClass } from "@/lib/utils/cell-input";
import {
  stickyHeadSecondRowClass,
  useStickyHeaderOffsets,
} from "@/lib/utils/sticky-table-header";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";
import { fetchAllCompanies } from "./components";
import type { Team } from "./contract-teams-drawer";
import {
  type ContractGroup,
  ContractGroupsList,
  ContractsPanelFrame,
  ContractsSectionTitle,
  contractsSaveMessage,
  contractsTableFrameClass,
  DeactivatedLabel,
  contractsHeadCellClass as headCellClass,
  contractsHeadGroupCellClass as headGroupCellClass,
  historyRemover,
  notifyDraftSaved,
  ReferentCell,
  type ReferentHistoryEntry,
  type ReferentHistoryRemoval,
  refusal,
  revealOnMount,
  contractsTeamsSeparatorClass as teamsSeparatorClass,
  useContractGroupsDraft,
  useDraftBaseline,
} from "./contracts-panel";
import { TeamMembersCell } from "./work-contracts-editable-panel";

export type OversightContractType = "amo" | "ct" | "sps" | "moe";

export const TYPE_LABELS: Record<OversightContractType, string> =
  CONTRACT_TYPE_LABELS;

export type OversightReferent = {
  id: number;
  employeeId: string;
  mail: string | null;
  function: string | null;
  isActive: boolean;
  employee: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    companyId: number;
  } | null;
};

export type OversightContractRow = {
  id: number;
  type: OversightContractType;
  shortName: string | null;
  isActive: boolean;
  delayForNoticeDays: number | null;
  groupId: number | null;
  performer: { id: number; name: string };
  customer: { id: number; name: string } | null;
  group: { id: number; name: string; shortName: string | null } | null;
  referent: OversightReferent | null;
  referentHistory: ReferentHistoryEntry[];
  batiments: Array<{ id: number; buildingId: number }>;
  teams: Team[];
};

type ContractDraft = {
  performerId: number;
  shortName: string;
  groupId: number | null;
  delayForNoticeDays: number | null;
};

type NewContractRow = {
  tempId: string;
  type: OversightContractType;
} & ContractDraft;

function generateTempId(): string {
  return `tmp-${Math.random().toString(36).slice(2)}`;
}

export const employeeName = (referent: OversightReferent): string => {
  const employee = referent.employee;
  if (isNil(employee)) return referent.employeeId;
  return (
    `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim() ||
    referent.employeeId
  );
};

const typeOptions: SelectOption[] = DO_CONTRACT_TYPES.map((type) => ({
  label: TYPE_LABELS[type],
  value: type,
}));

const optionLabel = (options: SelectOption[], value: number | null) =>
  options.find((option) => option.value === String(value))?.label;

const bodyCell = "p-1.5 align-top";
const abbreviationInvalidClass =
  "aria-invalid:border-red-500 aria-invalid:placeholder:text-red-600 dark:aria-invalid:placeholder:text-red-400";

/**
 * The cell itself says the abbreviation is missing: a message under the input
 * would break the fixed row height.
 */
const abbreviationProps = (value: string, dirty: boolean) => {
  const missing = dirty && value.trim() === "";
  return {
    required: true,
    "aria-label": "Abréviation",
    "aria-invalid": missing,
    placeholder: missing ? "Requis" : "Ex: CT",
  };
};
const savedFirstTitle =
  "Enregistrez ce contrat pour pouvoir gérer référent et équipes.";

/**
 * Screen 909 contracts panel, same layout as the 910 work-contracts panel
 * (shared `ContractsPanelFrame`): inline draft editing, Enregistrer/Annuler
 * in the fixed top bar (both confirmed), "+ Ajouter" rows for inline
 * creation, sticky title and header rows. After mise en service, contract
 * fields are read-only and actions switch to activate/deactivate; the
 * groupements stay editable in the draft. A refetch of `contracts` drops the
 * draft.
 */
export type OversightContractsEditablePanelProps = {
  yardId: number;
  contracts: OversightContractRow[];
  groups: ContractGroup[];
  /** Shows the groupements above the contracts (toggled in `toolbar`). */
  groupsExpanded: boolean;
  /** Says which groups the screen leaves out, and why. */
  groupsNote?: string;
  onDeleteGroup: (group: ContractGroup) => void;
  yardMoaCompany: { id: number; name: string } | null;
  isCommissioned: boolean;
  canAddContracts: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSuccess: () => Promise<void>;
  onManageTeams: (contract: OversightContractRow) => void;
  onSetReferent: (contract: OversightContractRow) => void;
  onRemoveReferent: (contract: OversightContractRow) => void;
  /**
   * After mise en service: deactivates the referent on the contract; the page
   * asks for the successors of their referent roles.
   */
  onDeactivateReferent: (contract: OversightContractRow) => void;
  /** The « x » of a former referent: removes them from the history. */
  onDeleteHistory?: (removal: ReferentHistoryRemoval) => void;
  onDeactivateContract: (contract: OversightContractRow) => void;
  onDelete: (contract: OversightContractRow) => void;
  /** Left part of the panel's fixed top bar (e.g. the groupements toggle). */
  toolbar?: ReactNode;
};

export function OversightContractsEditablePanel({
  yardId,
  contracts,
  groups,
  groupsExpanded,
  groupsNote,
  onDeleteGroup,
  yardMoaCompany,
  isCommissioned,
  canAddContracts,
  onDirtyChange,
  onSuccess,
  onManageTeams,
  onSetReferent,
  onRemoveReferent,
  onDeactivateReferent,
  onDeleteHistory,
  onDeactivateContract,
  onDelete,
  toolbar,
}: OversightContractsEditablePanelProps) {
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [draftContracts, setDraftContracts] = useState<
    Map<number, ContractDraft>
  >(new Map());
  const [newRows, setNewRows] = useState<NewContractRow[]>([]);
  const groupsDraft = useContractGroupsDraft(groups);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCompanies = useCallback(async () => {
    const { companies } = await fetchAllCompanies();
    if (isNotNil(companies)) {
      setCompanyOptions(
        companies.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      );
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const groupOptions: SelectOption[] = [
    { label: "Aucun groupement", value: "0" },
    ...groupsDraft.groups.map((g) => ({ label: g.name, value: String(g.id) })),
  ];

  const serverDraft = (contract: OversightContractRow): ContractDraft => ({
    performerId: contract.performer.id,
    shortName: contract.shortName ?? "",
    groupId: contract.groupId,
    delayForNoticeDays: contract.delayForNoticeDays,
  });

  const getDraft = (contract: OversightContractRow): ContractDraft =>
    draftContracts.get(contract.id) ?? serverDraft(contract);

  const isDirtyRow = (contract: OversightContractRow) =>
    draftContracts.has(contract.id);

  const onFieldChange = (
    contract: OversightContractRow,
    patch: Partial<ContractDraft>,
  ) => {
    setDraftContracts((prev) => {
      const current = prev.get(contract.id) ?? serverDraft(contract);
      const next = { ...current, ...patch };
      const server = serverDraft(contract);
      const updated = new Map(prev);
      const sameAsServer =
        next.performerId === server.performerId &&
        next.shortName === server.shortName &&
        next.groupId === server.groupId &&
        next.delayForNoticeDays === server.delayForNoticeDays;
      if (sameAsServer) updated.delete(contract.id);
      else updated.set(contract.id, next);
      return updated;
    });
  };

  const addRow = () => {
    setNewRows((prev) => [
      ...prev,
      {
        tempId: generateTempId(),
        type: "amo",
        performerId: 0,
        shortName: "",
        groupId: null,
        delayForNoticeDays: null,
      },
    ]);
  };

  const onNewRowChange = (tempId: string, patch: Partial<NewContractRow>) => {
    setNewRows((prev) =>
      prev.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
    );
  };

  const removeNewRow = (tempId: string) => {
    setNewRows((prev) => prev.filter((row) => row.tempId !== tempId));
  };

  const isDirty =
    draftContracts.size > 0 || newRows.length > 0 || groupsDraft.isDirty;

  useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const cancelDraft = () => {
    setDraftContracts(new Map());
    setNewRows([]);
    groupsDraft.reset();
  };

  useDraftBaseline(contracts, cancelDraft);

  const canSave = () => {
    const incomplete = [...newRows, ...draftContracts.values()].some(
      (d) => !d.performerId || d.shortName.trim() === "",
    );
    if (incomplete) {
      toaster.error({
        title: "Erreur",
        description:
          "Merci de sélectionner une société contractante et de saisir une abréviation avant d'enregistrer.",
      });
      return false;
    }
    if (groupsDraft.hasUnnamedGroup) {
      toaster.error({
        title: "Erreur",
        description:
          "Merci de saisir le nom de chaque groupement avant d'enregistrer.",
      });
      return false;
    }
    return true;
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const refused: string[] = [];

      for (const [id, d] of draftContracts.entries()) {
        const { error } = await apiClient.PATCH("/api/admin/contracts/{id}", {
          params: { path: { id } },
          body: {
            performerId: d.performerId,
            shortName: d.shortName.trim() ? d.shortName.trim() : null,
            groupId: d.groupId,
            delayForNoticeDays: d.delayForNoticeDays,
          },
        });
        if (error) {
          const name = contracts.find((c) => c.id === id)?.performer.name;
          refused.push(refusal(`Contrat ${name ?? id}`, error));
        }
      }

      for (const row of newRows) {
        const { error } = await apiClient.POST("/api/admin/contracts", {
          body: {
            yardId,
            type: row.type,
            performerId: row.performerId,
            customerId: yardMoaCompany?.id ?? null,
            shortName: row.shortName.trim() ? row.shortName.trim() : null,
            groupId: row.groupId,
            delayForNoticeDays: row.delayForNoticeDays,
          },
        });
        if (error) {
          const name = optionLabel(companyOptions, row.performerId);
          refused.push(refusal(`Nouveau contrat ${name ?? ""}`.trim(), error));
        }
      }

      refused.push(...(await groupsDraft.save()));
      notifyDraftSaved(refused);
      await onSuccess();
    } finally {
      setIsSaving(false);
    }
  };

  const editable = !isCommissioned;
  const { titleRef, headerRef, style } = useStickyHeaderOffsets();

  return (
    <ContractsPanelFrame
      toolbar={toolbar}
      isDirty={isDirty}
      isSaving={isSaving}
      canSave={canSave}
      saveMessage={contractsSaveMessage(
        "Les modifications apportées aux contrats donneurs d'ordre vont être enregistrées.",
        contracts,
        draftContracts,
      )}
      onSave={save}
      onDiscard={cancelDraft}
    >
      {groupsExpanded && (
        <ContractGroupsList
          groups={groupsDraft.groups}
          candidatesFor={(group) =>
            contracts
              .filter((c) => getDraft(c).groupId === group.id)
              .map((c) => ({ id: c.id, name: c.performer.name }))
          }
          onRename={(group, patch) => groupsDraft.change(group.id, patch)}
          isDirty={groupsDraft.isGroupDirty}
          onDelete={onDeleteGroup}
          onSetMandataire={(groupId, contractId) =>
            groupsDraft.change(groupId, { mandataireContractId: contractId })
          }
          note={groupsNote}
        />
      )}

      <section style={style}>
        <ContractsSectionTitle
          title="Contrats donneurs d'ordre"
          stickyRef={titleRef}
          subtitle={
            <>
              <p className="text-xs text-zinc-500">
                Client (maître d'ouvrage du chantier) :{" "}
                {yardMoaCompany?.name ?? "aucun maître d'ouvrage défini"}
              </p>
              {isCommissioned && (
                <p className="text-xs text-zinc-500">
                  {canAddContracts
                    ? "Chantier mis en service : les contrats existants ne sont plus modifiables, ils peuvent uniquement être désactivés. Vous pouvez toujours ajouter un contrat."
                    : "Chantier mis en service : les contrats ne sont plus modifiables, ils peuvent uniquement être désactivés."}
                </p>
              )}
            </>
          }
          action={
            canAddContracts && (
              <Button onClick={addRow}>
                <PlusIcon data-slot="icon" className="size-4" />
                Ajouter
              </Button>
            )
          }
        />

        {contracts.length === 0 && newRows.length === 0 ? (
          <div className="flex min-h-[100px] items-center justify-center rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 dark:border-zinc-700">
            Aucun contrat donneur d'ordre
          </div>
        ) : (
          // No scrolling wrapper: the header cells pin to the panel's scroll
          // area (`overflow-clip` rounds the corners without creating one).
          <div className={contractsTableFrameClass}>
            {/* Widths per .agents/screen-column-widths.md (909): characters
                plus the cell's controls and action buttons; Société
                contractante takes the rest of the panel. */}
            <table className="w-full table-fixed border-collapse text-xs [&_[data-part=value-text]]:truncate [&_[data-part=value-text]]:text-left">
              <colgroup>
                <col style={{ width: "calc(16ch + 12px)" }} />
                <col />
                <col style={{ width: "calc(8ch + 28px)" }} />
                <col style={{ width: "calc(14ch + 56px)" }} />
                <col style={{ width: "calc(10ch + 12px)" }} />
                <col style={{ width: "calc(16ch + 60px)" }} />
                <col style={{ width: "calc(12ch + 40px)" }} />
                <col style={{ width: "calc(20ch + 12px)" }} />
              </colgroup>
              <thead ref={headerRef}>
                <tr>
                  <th colSpan={6} className={headGroupCellClass}>
                    Contrat
                  </th>
                  <th
                    colSpan={2}
                    className={cn(headGroupCellClass, teamsSeparatorClass)}
                  >
                    Équipes
                  </th>
                </tr>
                <tr>
                  <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                    Type
                  </th>
                  <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                    Société contractante
                  </th>
                  <th
                    className={cn(stickyHeadSecondRowClass, headCellClass)}
                    title="Abréviation"
                  >
                    Abrév.
                  </th>
                  <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                    Groupement
                  </th>
                  <th
                    className={cn(stickyHeadSecondRowClass, headCellClass)}
                    title="Délai/visa (jours)"
                  >
                    Délai/visa
                  </th>
                  <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                    Référent contrat
                  </th>
                  <th
                    className={cn(
                      stickyHeadSecondRowClass,
                      headCellClass,
                      teamsSeparatorClass,
                    )}
                  >
                    Désignation
                  </th>
                  <th className={cn(stickyHeadSecondRowClass, headCellClass)}>
                    Membres
                  </th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => {
                  const draft = getDraft(contract);
                  const dirty = isDirtyRow(contract);
                  const referent = contract.referent;
                  // One row per team (a placeholder row without team); the
                  // contract cells span them.
                  const teamRows: (Team | null)[] =
                    contract.teams.length > 0 ? contract.teams : [null];
                  const rowSpan = teamRows.length;
                  return teamRows.map((team, index) => (
                    <tr
                      key={
                        isNil(team) ? contract.id : `${contract.id}-${team.id}`
                      }
                      className={cn(
                        "border-t border-zinc-100 dark:border-zinc-800",
                        dirty && "bg-amber-50 dark:bg-amber-900/20",
                      )}
                    >
                      {index === 0 && (
                        <>
                          <td rowSpan={rowSpan} className={bodyCell}>
                            <div className="flex items-center gap-2">
                              <span
                                className="truncate"
                                title={TYPE_LABELS[contract.type]}
                              >
                                {TYPE_LABELS[contract.type]}
                              </span>
                            </div>
                          </td>
                          {/* Contract actions live in the company cell,
                              as on 910 (PO, N65 14/10). */}
                          <td rowSpan={rowSpan} className={bodyCell}>
                            <div className="flex items-start justify-between gap-1">
                              <div className="min-w-0 flex-1">
                                {editable ? (
                                  <Select
                                    flat
                                    options={companyOptions}
                                    title={optionLabel(
                                      companyOptions,
                                      draft.performerId,
                                    )}
                                    value={
                                      draft.performerId
                                        ? [String(draft.performerId)]
                                        : []
                                    }
                                    onValueChange={(details) => {
                                      const val = details.value[0];
                                      onFieldChange(contract, {
                                        performerId: val ? Number(val) : 0,
                                      });
                                    }}
                                    placeholder="Société"
                                  />
                                ) : (
                                  <span
                                    className="block truncate px-2"
                                    title={contract.performer.name}
                                  >
                                    {contract.performer.name}
                                  </span>
                                )}
                              </div>
                              {isCommissioned ? (
                                contract.isActive ? (
                                  <Button
                                    plain
                                    onClick={() =>
                                      onDeactivateContract(contract)
                                    }
                                    aria-label="Désactiver le contrat"
                                    className="shrink-0"
                                  >
                                    <ProhibitIcon className="size-4" />
                                  </Button>
                                ) : (
                                  <DeactivatedLabel />
                                )
                              ) : (
                                <Button
                                  plain
                                  onClick={() => onDelete(contract)}
                                  aria-label="Supprimer"
                                  className="shrink-0"
                                >
                                  <TrashIcon className="size-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                          <td rowSpan={rowSpan} className={bodyCell}>
                            {editable ? (
                              <input
                                type="text"
                                value={draft.shortName}
                                onChange={(e) =>
                                  onFieldChange(contract, {
                                    shortName: e.target.value,
                                  })
                                }
                                maxLength={50}
                                className={cn(
                                  inputClass(dirty),
                                  abbreviationInvalidClass,
                                )}
                                {...abbreviationProps(draft.shortName, dirty)}
                              />
                            ) : (
                              <span className="px-2">
                                {contract.shortName ?? "-"}
                              </span>
                            )}
                          </td>
                          <td rowSpan={rowSpan} className={bodyCell}>
                            {editable ? (
                              <Select
                                flat
                                options={groupOptions}
                                title={optionLabel(groupOptions, draft.groupId)}
                                value={
                                  draft.groupId ? [String(draft.groupId)] : []
                                }
                                onValueChange={(details) => {
                                  const val = details.value[0];
                                  onFieldChange(contract, {
                                    groupId:
                                      val && val !== "0" ? Number(val) : null,
                                  });
                                }}
                                placeholder=""
                              />
                            ) : (
                              <span
                                className="block truncate px-2"
                                title={contract.group?.name}
                              >
                                {contract.group?.name ?? "-"}
                              </span>
                            )}
                          </td>
                          <td rowSpan={rowSpan} className={bodyCell}>
                            {editable ? (
                              <input
                                type="number"
                                min={0}
                                value={draft.delayForNoticeDays ?? ""}
                                onChange={(e) =>
                                  onFieldChange(contract, {
                                    delayForNoticeDays:
                                      e.target.value === ""
                                        ? null
                                        : Number(e.target.value),
                                  })
                                }
                                className={inputClass(dirty)}
                              />
                            ) : (
                              <span className="px-2">
                                {isNotNil(contract.delayForNoticeDays)
                                  ? String(contract.delayForNoticeDays)
                                  : "-"}
                              </span>
                            )}
                          </td>
                          <td rowSpan={rowSpan} className={bodyCell}>
                            <ReferentCell
                              referent={referent}
                              history={contract.referentHistory}
                              onDeleteHistory={historyRemover(
                                onDeleteHistory,
                                contract.id,
                              )}
                              actions={
                                <>
                                  <Button
                                    plain
                                    onClick={() => onSetReferent(contract)}
                                    aria-label="Définir le référent"
                                  >
                                    <UserPlusIcon className="size-4" />
                                  </Button>
                                  {isNotNil(referent) &&
                                    (isCommissioned ? (
                                      referent.isActive && (
                                        <Button
                                          plain
                                          onClick={() =>
                                            onDeactivateReferent(contract)
                                          }
                                          aria-label="Désactiver le référent"
                                        >
                                          <ProhibitIcon className="size-3.5" />
                                        </Button>
                                      )
                                    ) : (
                                      <Button
                                        plain
                                        onClick={() =>
                                          onRemoveReferent(contract)
                                        }
                                        aria-label="Retirer le référent"
                                      >
                                        <TrashIcon className="size-3.5" />
                                      </Button>
                                    ))}
                                </>
                              }
                            />
                          </td>
                        </>
                      )}
                      <td className={cn(bodyCell, teamsSeparatorClass)}>
                        <div className="flex items-center justify-between gap-1">
                          {isNil(team) ? (
                            <span className="text-zinc-400">-</span>
                          ) : (
                            <span
                              className="min-w-0 truncate font-medium text-zinc-700 dark:text-zinc-300"
                              title={team.name}
                            >
                              {team.name}
                            </span>
                          )}
                          {index === 0 && (
                            <Button
                              plain
                              onClick={() => onManageTeams(contract)}
                              aria-label="Gérer les équipes"
                              className="shrink-0"
                            >
                              <UsersThreeIcon className="size-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className={bodyCell}>
                        {isNil(team) ? (
                          <span className="text-zinc-400">-</span>
                        ) : (
                          <TeamMembersCell
                            team={team}
                            onDeleteHistory={historyRemover(
                              onDeleteHistory,
                              contract.id,
                              team.name,
                            )}
                          />
                        )}
                      </td>
                    </tr>
                  ));
                })}

                {newRows.map((row) => (
                  <tr
                    key={row.tempId}
                    ref={revealOnMount}
                    className="border-t border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20"
                  >
                    <td className={bodyCell}>
                      <Select
                        flat
                        options={typeOptions}
                        title={TYPE_LABELS[row.type]}
                        value={[row.type]}
                        onValueChange={(details) => {
                          const val = details.value[0];
                          if (val) {
                            onNewRowChange(row.tempId, {
                              type: val as OversightContractType,
                            });
                          }
                        }}
                      />
                    </td>
                    <td className={bodyCell}>
                      <div className="flex items-center gap-1">
                        <div className="min-w-0 flex-1">
                          <Select
                            flat
                            options={companyOptions}
                            title={optionLabel(companyOptions, row.performerId)}
                            value={
                              row.performerId ? [String(row.performerId)] : []
                            }
                            onValueChange={(details) => {
                              const val = details.value[0];
                              onNewRowChange(row.tempId, {
                                performerId: val ? Number(val) : 0,
                              });
                            }}
                            placeholder="Société"
                          />
                        </div>
                        <Button
                          plain
                          onClick={() => removeNewRow(row.tempId)}
                          aria-label="Annuler l'ajout du contrat"
                          className="shrink-0"
                        >
                          <XIcon className="size-4" />
                        </Button>
                      </div>
                    </td>
                    <td className={bodyCell}>
                      <input
                        type="text"
                        value={row.shortName}
                        onChange={(e) =>
                          onNewRowChange(row.tempId, {
                            shortName: e.target.value,
                          })
                        }
                        maxLength={50}
                        className={cn(
                          inputClass(true),
                          abbreviationInvalidClass,
                        )}
                        {...abbreviationProps(row.shortName, true)}
                      />
                    </td>
                    <td className={bodyCell}>
                      <Select
                        flat
                        options={groupOptions}
                        title={optionLabel(groupOptions, row.groupId)}
                        value={row.groupId ? [String(row.groupId)] : []}
                        onValueChange={(details) => {
                          const val = details.value[0];
                          onNewRowChange(row.tempId, {
                            groupId: val && val !== "0" ? Number(val) : null,
                          });
                        }}
                        placeholder=""
                      />
                    </td>
                    <td className={bodyCell}>
                      <input
                        type="number"
                        min={0}
                        value={row.delayForNoticeDays ?? ""}
                        onChange={(e) =>
                          onNewRowChange(row.tempId, {
                            delayForNoticeDays:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          })
                        }
                        className={inputClass(true)}
                      />
                    </td>
                    <td
                      className={cn(bodyCell, "text-zinc-400")}
                      title={savedFirstTitle}
                    >
                      —
                    </td>
                    <td
                      className={cn(
                        bodyCell,
                        teamsSeparatorClass,
                        "text-zinc-400",
                      )}
                      title={savedFirstTitle}
                    >
                      —
                    </td>
                    <td
                      className={cn(bodyCell, "text-zinc-400")}
                      title={savedFirstTitle}
                    >
                      —
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </ContractsPanelFrame>
  );
}
